import { z } from 'zod'

import { GROUP_STATUSES } from '@/features/groups/constants'

/**
 * The collection screens keep their state in the URL, for the same reason the customer and group
 * screens do: a filtered worklist is a place. "Which of Kaneshie's groups met this week and have
 * not been keyed?" should be a link somebody can send, and pressing Back out of a sheet should
 * land on the page of the worklist it was opened from.
 *
 * Every value is optional and every value is read back through `.catch()`, so a hand-edited or
 * stale URL degrades to the default rather than throwing an operator out of a screen with forty
 * keyed amounts on it.
 *
 * These are exported for the integrator to hand to `validateSearch` when the routes are
 * registered; until then `useCollectionSearch` reads the raw search through the same schemas, so
 * wiring them changes where the validation happens and nothing else.
 */

export const DEFAULT_PAGE_SIZE = 25

export const collectionWorklistSearchSchema = z.object({
  search: z.string().trim().min(1).optional().catch(undefined),
  status: z.enum(GROUP_STATUSES).optional().catch(undefined),
  branchId: z.string().min(1).optional().catch(undefined),
  loanOfficerId: z.string().min(1).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type CollectionWorklistSearch = z.infer<typeof collectionWorklistSearchSchema>

export const MEETING_STATUSES = [
  'Scheduled',
  'InProgress',
  'Completed',
  'Missed',
  'Cancelled',
] as const

export const groupMeetingsSearchSchema = z.object({
  /** ISO dates. A branch keys a week of sheets at a time, so the window is the primary filter. */
  from: z.string().date().optional().catch(undefined),
  to: z.string().date().optional().catch(undefined),
  status: z.enum(MEETING_STATUSES).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type GroupMeetingsSearch = z.infer<typeof groupMeetingsSearchSchema>

export const COLLECTION_SHEET_TABS = ['sheet', 'reconciliation'] as const

export type CollectionSheetTab = (typeof COLLECTION_SHEET_TABS)[number]

export const collectionSheetSearchSchema = z.object({
  tab: z.enum(COLLECTION_SHEET_TABS).optional().catch(undefined),
})

export type CollectionSheetSearch = z.infer<typeof collectionSheetSearchSchema>
